import {
  Modal,
  Button,
  FormField,
  SpaceBetween,
  Alert,
} from '@cloudscape-design/components';
import { useState, useEffect } from 'react';
import RequiredFieldLabel from '../RequiredFieldLabel';
import { useAudits } from 'src/store/audits/useAudits';
import useUserProfile from 'src/hooks/useUserProfile';
import { ASSIGNEE_LABELS, ERROR_MESSAGES } from 'src/contstant/commonLabels';
import EmployeePicker from '../common/EmployeePicker';

interface AddAssigneeModalProps {
  onSubmit: (userId: string) => void;
  onDismiss: () => void;
  visible: boolean;
  assignee?: string;
  auditId: string;
  businessUnitId: string;
}

function AddAssigneeModal({
  assignee,
  visible,
  onDismiss,
  onSubmit,
  auditId,
  businessUnitId,
}: AddAssigneeModalProps) {
  const [userId, setUserId] = useState('');
  const [validationError, setValidationError] = useState('');
  const { updateAudit, updateAuditApiStatus, resetApiStatus } = useAudits();
  const { getUserId, getUserProgramId } = useUserProfile();
  useEffect(() => {
    if (updateAuditApiStatus.isSuccess && visible) {
      onSubmit(userId);
      setUserId('');
      setValidationError('');
      resetApiStatus();
    }
  }, [updateAuditApiStatus.isSuccess, userId, onSubmit]);

  useEffect(() => {
    if (!visible) {
      setUserId('');
      setValidationError('');
    } else {
      if (assignee) {
        setUserId(assignee);
      }
    }
  }, [visible, assignee]);

  const handleSubmit = async () => {
    // EmployeePicker only commits a login when the user selects a PAPI
    // suggestion, so presence is the only check we need here.
    if (!userId) {
      setValidationError(ERROR_MESSAGES.ASSIGNEE_REQUIRED);
      return;
    }

    setValidationError('');
    updateAudit({
      auditId,
      assignees: [userId],
      businessUnitId: businessUnitId,
      lastUpdatedBy: getUserId(),
    });
  };

  return (
    <Modal
      data-testid="add-assignee-modal"
      visible={visible}
      onDismiss={() => onDismiss()}
      header={assignee ? 'Change Assignee' : 'Add Assignee'}
      closeAriaLabel="Close modal"
      footer={
        <SpaceBetween direction="horizontal" size="xs" data-testid="modal-footer">
          <Button variant="link" onClick={() => onDismiss()} data-testid="cancel-button">
            Cancel
          </Button>
          <Button
            data-testid="save-button"
            variant="primary"
            onClick={handleSubmit}
            loading={updateAuditApiStatus.isLoading}
          >
            Save
          </Button>
        </SpaceBetween>
      }
    >
      <SpaceBetween direction="vertical" size="l" data-testid="modal-content">
        <FormField
          data-testid="user-id-field"
          label={<RequiredFieldLabel label={ASSIGNEE_LABELS.LABEL} />}
          description={
            assignee
              ? 'Please add a valid User for this Audit'
              : 'Please add an assignee to Answer Audit Question'
          }
          errorText={validationError}
        >
          <EmployeePicker
            programId={getUserProgramId()}
            value={userId}
            data-testid="user-id-input"
            onSelect={(login) => {
              setUserId(login);
              if (validationError) setValidationError('');
            }}
            onClear={() => setUserId('')}
            placeholder={ASSIGNEE_LABELS.PLACEHOLDER}
            invalid={!!validationError}
          />
        </FormField>

        {updateAuditApiStatus.errorMessage && (
          <Alert type="error" data-testid="error-alert">
            {updateAuditApiStatus.errorMessage}
          </Alert>
        )}
      </SpaceBetween>
    </Modal>
  );
}

export default AddAssigneeModal;
